import { ExpressionModel } from '../Models/ExpressionModel';
import { ExpressionTypes } from '../Models/ExpressionTypes';
import { JsonSchemaModel } from '../Models/JsonSchemaModel';
import { getSchemaAlternatives, schemaMatchesValue } from './JsonSchemaTypes';

export function matchesExpressionSchema(schema: JsonSchemaModel, expression: ExpressionModel | undefined): boolean {
	if (!expression) return false;
	if (schema.anyOf || schema.oneOf || Array.isArray(schema.type))
		return getSchemaAlternatives(schema).some((branch) => matchesExpressionSchema(branch, expression));
	if (expression.Type === ExpressionTypes.Const) return schemaMatchesValue(schema, expression.Value);
	if (expression.Type === ExpressionTypes.Collection)
		return (
			schema.type === 'array' &&
			(!schema.items ||
				Array.isArray(schema.items) ||
				(expression.Items ?? []).every(
					(item: ExpressionModel) => !['Const', 'Object', 'Collection'].includes(item.Type) || matchesExpressionSchema(schema.items as JsonSchemaModel, item),
				))
		);
	if (expression.Type !== ExpressionTypes.Object) return false;
	if (schema.type !== 'object') return false;
	return Object.entries(schema.properties ?? {}).every(([name, property]) => !('const' in property) || expression.Properties?.[name]?.Value === property.const);
}

/** Keep compatible user values when changing variants; initialize only discriminator constants. */
export function selectSchemaVariant(schema: JsonSchemaModel, expression?: ExpressionModel): ExpressionModel | undefined {
	if ('const' in schema) return { Type: ExpressionTypes.Const, Value: schema.const };
	if (schema.type === 'null') return { Type: ExpressionTypes.Const, Value: null };
	if (expression && expression.Type !== ExpressionTypes.Object && matchesExpressionSchema(schema, expression)) return expression;
	if (schema.type === 'object') {
		const properties: Record<string, ExpressionModel> = {};
		for (const [name, current] of Object.entries(expression?.Properties ?? {}) as [string, ExpressionModel][]) {
			if (name in (schema.properties ?? {}) || schema.additionalProperties === false) continue;
			if (
				typeof schema.additionalProperties !== 'object' ||
				matchesExpressionSchema(schema.additionalProperties, current) ||
				!['Const', 'Object', 'Collection'].includes(current.Type)
			)
				properties[name] = current;
		}
		for (const [name, property] of Object.entries(schema.properties ?? {})) {
			if ('const' in property) properties[name] = { Type: ExpressionTypes.Const, Value: property.const };
			else if (expression?.Properties?.[name]) {
				const current = expression.Properties[name];
				if (matchesExpressionSchema(property, current) || !['Const', 'Object', 'Collection'].includes(current.Type)) properties[name] = current;
			}
		}
		return { ...(expression?.Type === ExpressionTypes.Object ? expression : {}), Type: ExpressionTypes.Object, Properties: properties };
	}
	if (schema.type === 'array') return { Type: ExpressionTypes.Collection, Items: [] };
	return undefined;
}
